import { z } from 'zod';
import { accountSchema, audioHandleSchema, MimoError, plansSchema, preferencesSchema, TRANSFER_FAILURE } from './contracts';
import type { SynthesisInput, VoicePreferences } from './contracts';

const SAFE_ERRORS = new Set([
  '请先登录', '无权访问', '余额不足，请先充值', '当前官方套餐不支持语音功能',
  '当前套餐语音额度已用尽', '语音订阅已并入官方套餐',
  '当前账号或 IP 的体验卡购买次数已达上限', '体验卡仅限尚未购买正式语音套餐的新用户',
  '语音请求过于频繁，请稍后再试', '当前套餐不可购买', '订单标识已用于其他套餐',
  '朗读文本过长', '参考音频必须为有效的 WAV 或 MP3，且不超过上传限制',
  '请上传参考音频并确认已获音色授权', '请先保存音色设计描述', '请求参数无效',
]);
export interface MimoClientOptions { baseUrl?: string; fetch?: typeof fetch; timeoutMs?: number; }
export class MimoClient {
  private readonly baseUrl: string;
  private readonly fetcher: typeof fetch;
  private readonly timeoutMs: number;
  constructor(options: MimoClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() ?? '/api').replace(/\/+$/, '') || '/api';
    this.fetcher = options.fetch ?? ((...args) => globalThis.fetch(...args));
    this.timeoutMs = options.timeoutMs ?? 150000;
  }
  private async request<T>(path: string, parse: (response: Response) => Promise<T>, method = 'GET', body?: unknown, signal?: AbortSignal): Promise<T> {
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) controller.abort();
    const timer = setTimeout(abort, this.timeoutMs);
    try {
      const response = await this.fetcher(`${this.baseUrl}/v1/tts${path}`, {
        method, credentials: 'include', cache: 'no-store', signal: controller.signal,
        headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      if (!response.ok) {
        const payload: unknown = await response.json().catch(() => null);
        const candidate = payload !== null && typeof payload === 'object' && 'error' in payload ? payload.error : undefined;
        throw new MimoError(typeof candidate === 'string' && SAFE_ERRORS.has(candidate) ? candidate : TRANSFER_FAILURE, response.status);
      }
      return await parse(response);
    } catch (error) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      if (error instanceof MimoError) throw error;
      throw new MimoError(TRANSFER_FAILURE);
    } finally {
      clearTimeout(timer); signal?.removeEventListener('abort', abort);
    }
  }
  private json<T>(path: string, schema: z.ZodType<T>, method?: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request(path, async (response) => schema.parse(await response.json()), method, body, signal);
  }
  plans() { return this.json('/catalog', plansSchema); }
  account(signal?: AbortSignal) { return this.json('/account', accountSchema, 'GET', undefined, signal); }
  savePreferences(preferences: VoicePreferences) { return this.json('/preferences', z.object({ preferences: preferencesSchema }), 'PUT', preferences); }
  synthesize(input: SynthesisInput, signal?: AbortSignal) { return this.json('/synthesize', audioHandleSchema, 'POST', input, signal); }
  download(id: string, signal?: AbortSignal) { return this.request(`/audio/${encodeURIComponent(id)}`, (response) => response.blob(), 'GET', undefined, signal); }
  acknowledge(id: string, sha256: string) { return this.json(`/audio/${encodeURIComponent(id)}/saved`, z.object({ ok: z.literal(true) }), 'POST', { sha256 }); }
  clearTemporary() { return this.json('/temporary', z.object({ ok: z.literal(true) }), 'DELETE'); }
}
export const mimoClient = new MimoClient();
